import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { MaskLine, Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", message: "", website: "" };

const Field = ({ label, id, textarea, ...props }) => {
  const C = textarea ? "textarea" : "input";
  return (
    <label htmlFor={id} className="group block text-left">
      <span className="text-xs font-medium tracking-widest uppercase text-muted-foreground">{label}</span>
      <C id={id} {...props} data-testid={`contact-${id}-input`}
        className={`mt-2 w-full border-0 border-b border-input bg-transparent px-0 py-3 text-base md:text-lg text-foreground placeholder:text-muted-foreground/70 outline-none transition-colors focus:border-foreground ${textarea ? "min-h-[130px] resize-y" : ""}`} />
    </label>
  );
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, company: form.company || null });
      setState("done");
      setForm(EMPTY);
      toast.success("Vielen Dank! Ihre Anfrage wurde gesendet.");
    } catch (err) {
      setState("idle");
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bitte prüfen Sie Ihre Eingaben (Nachricht mind. 10 Zeichen).");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-secondary/40" data-testid="contact-section">
      <div className="mx-auto max-w-6xl px-6 md:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-16">
        <div>
          <Reveal><Eyebrow className="mb-8">Kontakt</Eyebrow></Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6">
            <MaskLine>Projekt</MaskLine>
            <MaskLine delay={0.12}><span className="italic">anfragen</span></MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-muted-foreground text-base md:text-lg mb-10">Sie haben ein spannendes Projekt? Schreiben Sie mir.</p>
            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-lg bg-card border border-border/60" data-testid="contact-email-display">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <span className="text-base md:text-lg font-medium">advisory [ at ] schneider-ludorff.de</span>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="rounded-xl bg-card border border-border/60 p-7 md:p-10 space-y-7 shadow-[0_2px_20px_-4px_hsl(220_20%_10%/0.08)]" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-7">
              <Field label="Name" id="name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Ihr Name" />
              <Field label="E-Mail" id="email" type="email" required value={form.email} onChange={set("email")} placeholder="name@firma.de" />
            </div>
            <Field label="Unternehmen (optional)" id="company" value={form.company} onChange={set("company")} placeholder="Start-up / Firma" />
            <Field label="Nachricht" id="message" textarea required minLength={10} value={form.message} onChange={set("message")} placeholder="Erzählen Sie kurz von Ihrem Projekt …" />
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden />
            <p className="text-xs text-muted-foreground">
              Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben gemäß der <a href="/datenschutz" className="underline underline-offset-2 hover:text-foreground">Datenschutzerklärung</a> zu.
            </p>
            <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={state === "loading"} data-testid="contact-submit-button"
              className="group inline-flex h-14 w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-primary px-10 text-base font-medium text-primary-foreground transition-[transform,box-shadow,opacity] duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60">
              {state === "loading" ? <><Loader2 className="h-5 w-5 animate-spin" /> Wird gesendet …</>
                : state === "done" ? <><Check className="h-5 w-5" /> Gesendet – danke!</>
                : <>Anfrage senden <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
